import { MinusIcon, AddIcon } from "@chakra-ui/icons";
import {
  Box,
  HStack,
  Alert,
  AlertIcon,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Icon,
  VStack,
  FormControl,
  FormLabel,
  Text,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { DropDown, Option } from "../Select/DropDown";
import { NodeInputOutput } from "./NodePropertiesModal";
import { Editor, useMonaco } from "@monaco-editor/react";
import { useEffect } from "react";

export const DataProperties = ({
  processVariableOptions,
  nodeInputOutput,
  selectedInput,
  selectedOutput,
  setSelectedInput,
  setSelectedOutput,
  onNewExpression,
  onExpressionDelete,
  onExpressionEdit,
  onTargetSelect,
  defineGroovyLanguage,
  processModelMatchers,
  ruleMatchers,
  pvMatchers,
  reverseExpressionHandler,
}: {
  processVariableOptions: {
    id: any;
    name: string;
    component: JSX.Element;
  }[];
  nodeInputOutput: NodeInputOutput[];
  selectedInput: NodeInputOutput | undefined;
  selectedOutput: NodeInputOutput | undefined;
  setSelectedInput: React.Dispatch<
    React.SetStateAction<NodeInputOutput | undefined>
  >;
  setSelectedOutput: React.Dispatch<
    React.SetStateAction<NodeInputOutput | undefined>
  >;
  onNewExpression: (ioType: "I" | "O") => void;
  onExpressionDelete: (uuid: string) => void;
  onExpressionEdit: (
    e: string | undefined,
    selected: NodeInputOutput | undefined
  ) => void;
  onTargetSelect: (
    option: Option,
    selected: NodeInputOutput | undefined
  ) => void;
  defineGroovyLanguage: (
    monacoInstance: any,
    ruleMatchers: string[],
    pvMatchers: string[],
    processModelMatchers: string[]
  ) => void;
  processModelMatchers: string[];
  ruleMatchers: string[];
  pvMatchers: string[];
  reverseExpressionHandler: (expression: string) => string;
}) => {
  const monaco = useMonaco();

  useEffect(() => {
    if (monaco) {
      defineGroovyLanguage(
        monaco,
        ruleMatchers,
        pvMatchers,
        processModelMatchers
      );
    }
  }, [monaco]);

  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: false,
  };

  return (
    <Tabs>
      <TabList mx={-4}>
        <Tab
          color="white"
          _selected={{ color: "brand.500", borderColor: "brand.500" }}
        >
          Inputs
        </Tab>
        <Tab
          color="white"
          _selected={{ color: "brand.500", borderColor: "brand.500" }}
        >
          Outputs
        </Tab>
      </TabList>

      <TabPanels width={"100%"}>
        {/* Inputs Panel */}
        <TabPanel width={"100%"}>
          <Box>
            <HStack>
              <Alert
                status="warning"
                variant="subtle"
                textAlign="center"
                colorScheme="brand"
                borderRadius="md"
                fontSize="14px"
                w="100%"
                gap={2}
              >
                <Text fontSize={"16px"} fontWeight={"bold"}>
                  Node Inputs
                </Text>
                <AlertIcon />
                Use rules and process variables to define logic inputs
              </Alert>
            </HStack>
            <Box width={"100%"} display={"flex"} flexDirection={"row"}>
              <Flex
                width={"35%"}
                flexDirection={"column"}
                height={"45vh"}
                gap={2}
                p={2}
                m={2}
                border={"1px"}
                borderRadius={"md"}
              >
                <HStack>
                  <HStack onClick={() => onNewExpression("I")} gap={1}>
                    <Icon
                      as={FaPlus as React.ElementType}
                      size={"20px"}
                      color={"blue.500"}
                    />
                    <Text
                      fontSize={"14px"}
                      color={"brand.400"}
                      cursor="pointer"
                      _hover={{ color: "brand.300" }}
                    >
                      New Input
                    </Text>
                  </HStack>

                  {selectedInput && (
                    <HStack
                      onClick={() => onExpressionDelete(selectedInput.uuid)}
                      gap={1}
                    >
                      <Icon
                        as={IoClose as React.ElementType}
                        size={"20px"}
                        color={"red"}
                      />
                      <Text
                        fontSize={"14px"}
                        color={"red.400"}
                        cursor="pointer"
                        _hover={{ color: "red.300" }}
                      >
                        Delete
                      </Text>
                    </HStack>
                  )}
                </HStack>
                <VStack align={"start"} maxHeight={"100%"} overflow={"auto"}>
                  {nodeInputOutput
                    .filter((it) => it.ioType === "I")
                    .reverse()
                    .map((input) => (
                      <Box
                        key={input.uuid}
                        backgroundColor={
                          input.uuid === selectedInput?.uuid
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(255, 255, 255, 0.1)"
                        }
                        borderRadius="md"
                        width="100%"
                        p={2}
                      >
                        <Text
                          cursor="pointer"
                          onClick={() => setSelectedInput(input)}
                          noOfLines={1}
                          color={
                            input.uuid === selectedInput?.uuid
                              ? "white"
                              : "white"
                          }
                        >
                          {input.name !== ""
                            ? reverseExpressionHandler(input.name)
                            : "New Expression"}
                        </Text>
                      </Box>
                    ))}
                </VStack>
              </Flex>
              <Flex
                width={"65%"}
                flexDirection={"column"}
                height={"45vh"}
                gap={2}
                p={2}
                m={2}
                border={"1px"}
                borderRadius={"md"}
              >
                {selectedInput ? (
                  <Box>
                    <FormControl isRequired>
                      <HStack>
                        <FormLabel minWidth="15%" color="white">
                          Expression
                        </FormLabel>
                        <Box
                          width="70%"
                          borderWidth="1px"
                          borderRadius="md"
                          overflow="hidden"
                        >
                          {selectedInput && (
                            <Editor
                              key={selectedInput.uuid}
                              value={reverseExpressionHandler(
                                selectedInput.expression
                              )}
                              defaultLanguage="groovy"
                              theme="vs-dark"
                              height="25vh"
                              width="100%"
                              options={editorOptions}
                              onChange={(e) => {
                                console.log("onChange:", e);
                                onExpressionEdit(e, selectedInput);
                              }}
                            />
                          )}
                        </Box>
                      </HStack>
                    </FormControl>
                    <FormControl mt={3}>
                      <HStack>
                        <FormLabel minWidth="15%" color="white">
                          Target
                        </FormLabel>
                        <DropDown
                          selected={selectedInput?.target}
                          title={
                            <HStack justifyContent="space-between" p={2} />
                          }
                          options={processVariableOptions}
                          onOptionChange={(selectedOption) =>
                            onTargetSelect(
                              selectedOption as Option,
                              selectedInput
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Box>
                ) : (
                  <Box height={"100%"}>
                    <VStack justifyContent={"center"} height={"100%"}>
                      <Text color="white">
                        Please select a field to display its properties
                      </Text>
                    </VStack>
                  </Box>
                )}
              </Flex>
            </Box>
          </Box>
        </TabPanel>

        {/* Outputs Panel */}
        <TabPanel>
          <Box>
            <HStack>
              <Alert
                status="warning"
                variant="subtle"
                textAlign="center"
                colorScheme="brand"
                borderRadius="md"
                fontSize="14px"
                w="100%"
                gap={2}
              >
                <Text fontSize={"16px"} fontWeight={"bold"}>
                  Node Outputs
                </Text>
                <AlertIcon />
                Save node data to process variables for use elsewhere in the
                process
              </Alert>
            </HStack>
            <Box width={"100%"} display={"flex"} flexDirection={"row"}>
              <Flex
                width={"35%"}
                flexDirection={"column"}
                height={"45vh"}
                gap={2}
                p={2}
                m={2}
                border={"1px"}
                borderRadius={"md"}
              >
                <Accordion allowMultiple>
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box
                              as="span"
                              flex="1"
                              textAlign="left"
                              color="white"
                            >
                              Result
                            </Box>
                            {isExpanded ? (
                              <MinusIcon fontSize="12px" />
                            ) : (
                              <AddIcon fontSize="12px" />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack
                            align={"start"}
                            maxHeight={"100%"}
                            overflow={"auto"}
                          >
                            {nodeInputOutput
                              .filter((it) => it.ioType === "O" && it.isDefault)
                              .map((input) => (
                                <Box
                                  key={input.uuid}
                                  backgroundColor={
                                    input.uuid === selectedOutput?.uuid
                                      ? "rgba(255, 255, 255, 0.2)"
                                      : "rgba(255, 255, 255, 0.1)"
                                  }
                                  borderRadius="md"
                                  width="100%"
                                  p={2}
                                >
                                  <Text
                                    cursor="pointer"
                                    onClick={() => setSelectedOutput(input)}
                                    noOfLines={1}
                                    color="white"
                                  >
                                    {reverseExpressionHandler(input.name)}
                                  </Text>
                                </Box>
                              ))}
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>

                <Accordion allowMultiple defaultIndex={[0]}>
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box
                              as="span"
                              flex="1"
                              textAlign="left"
                              color="white"
                            >
                              Custom Outputs
                            </Box>
                            {isExpanded ? (
                              <MinusIcon fontSize="12px" />
                            ) : (
                              <AddIcon fontSize="12px" />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <HStack>
                            <HStack
                              onClick={() => onNewExpression("O")}
                              gap={1}
                            >
                              <Icon
                                as={FaPlus as React.ElementType}
                                size={"20px"}
                                color={"blue.500"}
                              />
                              <Text
                                fontSize={"14px"}
                                color={"brand.400"}
                                cursor="pointer"
                                _hover={{ color: "brand.300" }}
                              >
                                New Custom Output
                              </Text>
                            </HStack>

                            {selectedOutput && (
                              <HStack
                                onClick={() =>
                                  onExpressionDelete(selectedOutput.uuid)
                                }
                                gap={1}
                              >
                                <Icon
                                  as={IoClose as React.ElementType}
                                  size={"20px"}
                                  color={"red"}
                                />
                                <Text
                                  fontSize={"14px"}
                                  color={"red.400"}
                                  cursor="pointer"
                                  _hover={{ color: "red.300" }}
                                >
                                  Delete
                                </Text>
                              </HStack>
                            )}
                          </HStack>
                          <VStack
                            align={"start"}
                            maxHeight={"100%"}
                            overflow={"auto"}
                          >
                            {nodeInputOutput
                              .filter(
                                (it) => it.ioType === "O" && !it.isDefault
                              )
                              .reverse()
                              .map((input) => (
                                <Box
                                  key={input.uuid}
                                  backgroundColor={
                                    input.uuid === selectedOutput?.uuid
                                      ? "rgba(255, 255, 255, 0.2)"
                                      : "rgba(255, 255, 255, 0.1)"
                                  }
                                  borderRadius="md"
                                  width="100%"
                                  p={2}
                                >
                                  <Text
                                    cursor="pointer"
                                    onClick={() => setSelectedOutput(input)}
                                    noOfLines={1}
                                    color="white"
                                  >
                                    {input.name !== ""
                                      ? reverseExpressionHandler(input.name)
                                      : "New Expression"}
                                  </Text>
                                </Box>
                              ))}
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>
              </Flex>
              <Flex
                width={"65%"}
                flexDirection={"column"}
                height={"45vh"}
                gap={2}
                p={2}
                m={2}
                border={"1px"}
                borderRadius={"md"}
              >
                {selectedOutput ? (
                  <Box>
                    <FormControl isRequired>
                      <HStack>
                        <FormLabel minWidth="15%" color="white">
                          Expression
                        </FormLabel>
                        <Box
                          width="70%"
                          borderWidth="1px"
                          borderRadius="md"
                          overflow="hidden"
                        >
                          <Editor
                            key={selectedOutput?.uuid}
                            value={
                              selectedOutput?.isDefault
                                ? reverseExpressionHandler(
                                    selectedOutput.expression || ""
                                  )
                                : reverseExpressionHandler(
                                    selectedOutput.expression || ""
                                  )
                            }
                            defaultLanguage="groovy"
                            theme="vs-dark"
                            height="25vh"
                            width="100%"
                            options={{
                              ...editorOptions,
                              readOnly: selectedOutput?.isDefault,
                            }}
                            onChange={(newVal) => {
                              if (!selectedOutput?.isDefault) {
                                onExpressionEdit(newVal, selectedOutput);
                              }
                            }}
                          />
                        </Box>
                      </HStack>
                    </FormControl>
                    <FormControl mt={3}>
                      <HStack>
                        <FormLabel minWidth="15%" color="white">
                          Target
                        </FormLabel>
                        <DropDown
                          selected={selectedOutput?.target}
                          title={
                            <HStack justifyContent="space-between" p={2} />
                          }
                          options={processVariableOptions}
                          onOptionChange={(selectedOption) =>
                            onTargetSelect(
                              selectedOption as Option,
                              selectedOutput
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Box>
                ) : (
                  <Box height={"100%"}>
                    <VStack justifyContent={"center"} height={"100%"}>
                      <Text color="white">
                        Please select a field to display its properties
                      </Text>
                    </VStack>
                  </Box>
                )}
              </Flex>
            </Box>
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
